import { useQuery, type QueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/apiClient';
import { onboardingKeys } from '@/api/keys/onboardingKeys';

/**
 * D43a gate-line and prompt-frequency config, served on /onboarding/status/
 * so a gate-line rollback or a threshold tweak is a backend config change
 * rather than an app-store release. Absent only when the status response
 * itself is missing or malformed (see promptConfig-absent handling below).
 */
export interface OnboardingPromptConfig {
  quiz3PromptLifetimeDeclineLimit: number;
  nudgeMaxOccurrences: number;
  requiredQuizKeys: string[];
}

/**
 * Onboarding status response from the API
 */
export interface OnboardingStatus {
  /** Whether user has completed Quiz 1 (level1) */
  hasTakenInitialQuiz: boolean;
  /**
   * D43a — the gate. The first key in promptConfig.requiredQuizKeys that is
   * incomplete, or null once every required quiz is complete. This is the
   * single server-derived source of gate state (FR-A2); optional only for
   * pre-D43a test fixtures and legacy cache shapes that predate this field.
   */
  nextRequiredQuizKey?: string | null;
  /** D43a — gate line and prompt-frequency thresholds, served rather than compiled. */
  promptConfig?: OnboardingPromptConfig;
  /** Version of tour user has seen (null if never) */
  tourVersionSeen: string | null;
  /** Current app tour version */
  currentTourVersion: string;
  /** Whether user has completed the permissions onboarding step */
  permissionsCompleted: boolean;
  /** Whether user has completed the find friends onboarding step */
  friendsCompleted: boolean;
  /** Whether user has completed the profile setup onboarding step */
  profileCompleted: boolean;
}

/**
 * Computed onboarding state derived from API response
 */
export interface OnboardingState extends OnboardingStatus {
  /** Whether user needs to take the initial quiz */
  needsQuiz: boolean;
  /** Whether user needs to see the tour (new version available) */
  needsTour: boolean;
  /** Whether this is a returning user who needs the tour */
  isReturningUser: boolean;
  /** Whether user needs to complete the permissions step (new users only) */
  needsPermissions: boolean;
  /** Whether user needs to complete the find friends step (new users only) */
  needsFriends: boolean;
  /** Whether user needs to complete the profile setup step (new users only) */
  needsProfile: boolean;
}

/**
 * Transform API response to include computed fields
 */
function transformOnboardingStatus(data: OnboardingStatus): OnboardingState {
  // FR-A2 — needsQuiz derives from the server-served gate field alone, not
  // from hasTakenInitialQuiz. A member who has completed level1 but not
  // level2 must still report needsQuiz: true.
  const needsQuiz = (data.nextRequiredQuizKey ?? null) !== null;
  const needsTour = data.tourVersionSeen !== data.currentTourVersion;
  const isReturningUser = data.hasTakenInitialQuiz && needsTour;

  // FR-A3 — isNewUser keeps its narrow, unwidened meaning: "hasn't completed
  // quiz 1 (level1)". A member who has finished level1 but not level2 is NOT
  // new and must NOT be dragged back through permissions/profile/friends —
  // those three first-run steps are tied to level1 completion specifically,
  // not to the (now wider) gate.
  const isNewUser = !data.hasTakenInitialQuiz;
  const needsPermissions = isNewUser && !data.permissionsCompleted;
  const needsProfile = isNewUser && !data.profileCompleted;
  const needsFriends = isNewUser && !data.friendsCompleted;

  return {
    ...data,
    needsQuiz,
    needsTour,
    isReturningUser,
    needsPermissions,
    needsFriends,
    needsProfile,
  };
}

/**
 * Hook to get the current user's onboarding status
 */
export function useOnboardingStatus(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: onboardingKeys.status(),
    queryFn: async () => {
      const data = await apiFetch<OnboardingStatus>('/onboarding/status/');
      return transformOnboardingStatus(data);
    },
    enabled: options?.enabled,
    // D43a — this response now carries promptConfig (gate line + frequency
    // thresholds), not just status, so a gate-line rollback should reach an
    // already-open session reasonably fast. 30s is that propagation window:
    // an open session sees a rollback within ~30s of its next background
    // refetch; a fresh launch sees it immediately. (Was 5 minutes pre-D43a.)
    staleTime: 30 * 1000,
    // D43a — once the server returns a genuine 503 on failure (Phase 3),
    // TanStack Query's default retry/backoff would delay isError by several
    // seconds, making the guard's unavailable panel look like a hang. Fail
    // fast instead; Retry on the panel issues an explicit refetch rather than
    // waiting for another automatic attempt.
    retry: 1,
    retryDelay: 500,
  });
}

/**
 * D43a — the one shared answer to "may a gated member be on this path?".
 * The guard's effect, the guard's render path, and the tab bar's click
 * handler all call this instead of each carrying their own `/quizzes` check
 * (which is how the guard and tab bar came to disagree pre-D43a).
 *
 * Allowed while gated: the required quiz's own path (and any sub-path under
 * it), the `/quizzes` list (so the member can see the locked state), and
 * `/menu`. Any other `/quizzes/*` path is NOT allowed — it redirects to the
 * required quiz rather than passing through.
 *
 * D61 Phase 6 — `/quick-search` was removed from this allow-list when the
 * route was retired to a redirect-only stub (it now forwards to `/search`
 * before this check would ever matter).
 */
export function canGatedMemberAccessPath(
  pathname: string,
  nextRequiredQuizKey: string
): boolean {
  if (pathname.startsWith('/menu')) {
    return true;
  }
  if (pathname === '/quizzes' || pathname === '/quizzes/') {
    return true;
  }
  return pathname.startsWith(`/quizzes/${nextRequiredQuizKey}`);
}

/**
 * Given the quiz key that was just completed and the server-served, ordered
 * `requiredQuizKeys` gate line, returns the next required key that is not yet
 * complete, or `null` when the completed key was the last one in the line.
 *
 * Must not hard-code a `level1 -> level2` sequence — the whole point is that
 * the gate line is configurable and this derivation must honor it, including
 * a one-quiz gate line where completing that one quiz releases immediately.
 */
export function deriveNextRequiredQuizKey(
  completedQuizKey: string,
  requiredQuizKeys: string[]
): string | null {
  const completedIndex = requiredQuizKeys.indexOf(completedQuizKey);
  if (completedIndex === -1) {
    // Not a gate quiz at all — nothing to advance.
    return null;
  }
  const nextIndex = completedIndex + 1;
  return nextIndex < requiredQuizKeys.length ? requiredQuizKeys[nextIndex] : null;
}

/**
 * Synchronously advances the onboarding status cache's gate fields via
 * queryClient.setQueryData against onboardingKeys.status(), matching the
 * setQueryData-then-invalidateQueries shape used by useOnboardingMutations.
 * Called from QuizTaking's completeQuiz in place of the level1-literal block.
 */
export function stampGateProgress(
  queryClient: QueryClient,
  completedQuizKey: string,
  requiredQuizKeys: string[]
): void {
  const nextRequiredQuizKey = deriveNextRequiredQuizKey(completedQuizKey, requiredQuizKeys);

  queryClient.setQueryData<OnboardingState>(
    onboardingKeys.status(),
    (prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        nextRequiredQuizKey,
        needsQuiz: nextRequiredQuizKey !== null,
        // hasTakenInitialQuiz means "the first required quiz is complete" —
        // derived from the served gate line's own first entry, not a
        // hard-coded 'level1' literal, so it honors a reconfigured gate line.
        hasTakenInitialQuiz:
          prev.hasTakenInitialQuiz || completedQuizKey === requiredQuizKeys[0],
      };
    }
  );
  // Fire-and-forget: reconcile cache with server after the optimistic stamp.
  queryClient.invalidateQueries({ queryKey: onboardingKeys.status() });
}
